import React, { useRef, useState } from 'react';
import api from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/errors';

const BESCHRIFTUNG = {
  neu: { text: 'wird eingetragen', klasse: 'bg-emerald-50 text-emerald-700 border-0' },
  weicht_ab: { text: 'weicht ab', klasse: 'bg-amber-50 text-amber-700 border-0' },
  unveraendert: { text: 'unverändert', klasse: 'bg-slate-100 text-slate-500 border-0' },
  kein_profil: { text: 'kein Profil', klasse: 'bg-slate-100 text-slate-500 border-0' },
  mehrdeutig: { text: 'Name mehrdeutig', klasse: 'bg-amber-50 text-amber-700 border-0' },
};

/**
 * Bankverbindungen der Lehrkräfte aus einer Überweisungsdatei (pain.001).
 *
 * Die Datei wird zum Übernehmen ein zweites Mal hochgeladen. Das ist Absicht:
 * so kann der Serverstand zwischen Voransicht und Übernahme nicht auseinander
 * laufen, und es braucht keinen Zwischenspeicher.
 */
export default function BankImportDialog({ open, onClose, onDone }) {
  const [datei, setDatei] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [mitAbweichenden, setMitAbweichenden] = useState(false);
  const dateiFeld = useRef(null);

  const schliessen = () => {
    setDatei(null);
    setVorschau(null);
    setMitAbweichenden(false);
    onClose();
  };

  const waehlen = async (e) => {
    const gewaehlt = e.target.files?.[0] || null;
    setDatei(gewaehlt);
    setVorschau(null);
    if (!gewaehlt) return;
    setLaeuft(true);
    try {
      const daten = new FormData();
      daten.append('file', gewaehlt);
      const r = await api.post('/payroll/bank-import/preview', daten);
      setVorschau(r.data);
    } catch (err) {
      // `formatApiError` statt `detail` direkt: eine Validierungsantwort von
      // FastAPI liefert dort eine Liste von Objekten. Die an `toast` zu geben
      // hieß, React ein Objekt als Kind vorzusetzen — das beendete den
      // Renderbaum, und der Bildschirm blieb weiß.
      toast.error(formatApiError(err, 'Datei konnte nicht gelesen werden'));
      setDatei(null);
      if (dateiFeld.current) dateiFeld.current.value = '';
    } finally { setLaeuft(false); }
  };

  const uebernehmen = async () => {
    if (!datei) return;
    setLaeuft(true);
    try {
      const daten = new FormData();
      daten.append('file', datei);
      const r = await api.post(
        `/payroll/bank-import/commit?mit_abweichenden=${mitAbweichenden}`, daten);
      toast.success(`${r.data.uebernommen} Bankverbindungen eingetragen`);
      onDone?.();
      schliessen();
    } catch (err) {
      toast.error(formatApiError(err, 'Übernahme fehlgeschlagen'));
    } finally { setLaeuft(false); }
  };

  const zeilen = vorschau?.rows || [];
  const anzahl = vorschau?.summary || {};
  const uebernehmbar = (anzahl.neu || 0) + (mitAbweichenden ? (anzahl.weicht_ab || 0) : 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && schliessen()}>
      <DialogContent className="max-w-3xl" data-testid="payroll-bank-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5" /> Bankdaten aus Überweisungsdatei
          </DialogTitle>
          <DialogDescription>
            Eine Überweisungsdatei (pain.001) enthält zu jeder Zahlung den Namen der Lehrkraft,
            ihre IBAN und die BIC. Zugeordnet wird über den Namen; Umlaute schreibt eine
            SEPA-Datei um („Körbe" steht dort als „Koerbe"), das wird berücksichtigt.
          </DialogDescription>
        </DialogHeader>

        <div>
          <input
            ref={dateiFeld}
            type="file"
            accept=".xml"
            onChange={waehlen}
            className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-slate-100 file:text-sm"
            data-testid="payroll-bank-import-file"
          />
        </div>

        {laeuft && !vorschau && (
          <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
        )}

        {vorschau && (
          <>
            <p className="text-sm text-slate-600" data-testid="payroll-bank-import-summary">
              {vorschau.total} Empfänger in der Datei —{' '}
              <strong>{anzahl.neu || 0}</strong> ohne bisherige IBAN
              {anzahl.weicht_ab ? `, ${anzahl.weicht_ab} mit abweichender IBAN` : ''}
              {anzahl.unveraendert ? `, ${anzahl.unveraendert} unverändert` : ''}
              {anzahl.kein_profil ? `, ${anzahl.kein_profil} ohne Profil` : ''}
              {anzahl.mehrdeutig ? `, ${anzahl.mehrdeutig} mit mehrdeutigem Namen` : ''}.
            </p>

            <div className="max-h-72 overflow-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm" data-testid="payroll-bank-import-table">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-xs text-slate-500 uppercase tracking-wide">
                    <th className="text-left py-2 px-3">Empfänger</th>
                    <th className="text-left py-2 px-3">Profil</th>
                    <th className="text-left py-2 px-3">IBAN</th>
                    <th className="text-left py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((z, i) => {
                    const b = BESCHRIFTUNG[z.aktion] || { text: z.aktion, klasse: '' };
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1.5 px-3 text-slate-800">{z.name}</td>
                        <td className="py-1.5 px-3 text-slate-500">{z.profile_name || '—'}</td>
                        <td className="py-1.5 px-3 font-mono text-xs text-slate-600">{z.iban}</td>
                        <td className="py-1.5 px-3">
                          <Badge className={`text-[10px] ${b.klasse}`}>{b.text}</Badge>
                          {z.grund && <span className="ml-2 text-xs text-slate-400">{z.grund}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {anzahl.weicht_ab > 0 && (
              <label className="flex items-start gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={mitAbweichenden}
                  onChange={(e) => setMitAbweichenden(e.target.checked)}
                  className="mt-0.5"
                  data-testid="payroll-bank-import-overwrite"
                />
                <span>
                  Auch die {anzahl.weicht_ab} abweichenden IBANs überschreiben. Ohne Haken bleiben
                  sie stehen — eine gepflegte Bankverbindung kann neuer sein als die Datei.
                </span>
              </label>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={schliessen}>Abbrechen</Button>
          <Button
            onClick={uebernehmen}
            disabled={!vorschau || laeuft || uebernehmbar === 0}
            data-testid="payroll-bank-import-commit"
          >
            {laeuft && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {uebernehmbar} Bankverbindungen übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
